import { SyntheticEvent, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useMutation } from '@apollo/client';
import { ErrorResponse } from '@apollo/client/link/error';
import BootstrapForm from 'react-bootstrap/Form';
import { get } from 'lodash';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Dialog from 'components/common/Dialog/Dialog';
import { queryTimeRangeState, searchParamState } from 'components/common/Managers/RouteStateManager.state';
import { GET_CONFIGV2, LIST_DASHBOARD_NAMES, RENAME_DASHBOARD_V2 } from '../../queries';

import './DashboardSettings.scss';

/**
 * Modal dialog for editing the dashboard name and description.
 *
 * This is a bit messy due to the backend data modal. Dashboards are stored in a config file, whose name is the same
 * as the dashboard title. So, to edit the title, the config file must be renamed and the page redirected to use the new config file.
 *
 * @prop showModal          This modal is shown when true
 * @prop dashboardName      The dashboard config file name (and dashboard title)
 * @prop dashboardDesc      The dashboard description
 * @prop updateDescription  Callback for updating just the page's display of the description
 * @prop handleClose        Callback for when the modal is closed
 *
 */

type DashboardSettingsProps = {
  showModal: boolean;
  dashboardName: string;
  dashboardDesc: string;
  handleClose: () => void;
};

function DashboardSettings({
  showModal,
  dashboardName,
  dashboardDesc,
  handleClose,
}: DashboardSettingsProps): JSX.Element {
  const [name, setName] = useState(dashboardName);
  const [description, setDescription] = useState(dashboardDesc);
  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [saveChanges] = useMutation(RENAME_DASHBOARD_V2, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name, startTime, endTime } }, { query: LIST_DASHBOARD_NAMES }],
  });
  const [validated, setValidated] = useState(false);
  const [apiError, setApiError] = useState(null);

  const handleApiSuccess = (): void => {
    // if successful, refresh the entire page using the new name so that even the Dashboards dropdown menu is updated
    window.location.href = window.location.pathname + '?page=' + encodeURI(name);
  };

  const handleApiError = (error: ErrorResponse): void => {
    // if there was an error, extract the error message and show it
    const graphQLErrorMessage = get(error.graphQLErrors, '[0].message', '');
    setApiError(graphQLErrorMessage);
  };

  // HTML5 validation relies on using form submit
  const handleSubmit = (event: SyntheticEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    setApiError(null);
    setValidated(true); // So HTML form will show any errors
    const form = event.currentTarget as HTMLFormElement;
    if (form.checkValidity() !== false) {
      saveChanges({
        variables: {
          dashboardName,
          name,
          description,
          tabName,
        },
      })
        .then(handleApiSuccess)
        .catch(handleApiError);
    }
  };

  const onChangeName = (event: SyntheticEvent): void => {
    setValidated(false); // So HTML form won't show API errors
    const eventTarget = event.target as HTMLInputElement;
    setName(eventTarget.value);
  };

  const onChangeDescription = (event: SyntheticEvent): void => {
    const eventTarget = event.target as HTMLInputElement;
    setDescription(eventTarget.value);
  };

  // Before cancelling, reset the state in case the user brings up the dialog again
  const closeDialog = (): void => {
    setValidated(false);
    setName(dashboardName);
    setDescription(dashboardDesc);
    handleClose();
  };

  const buttons = {
    rightItems: [
      <Button
        variant={ButtonVariant.PRIMARY}
        type="submit"
        submitForm={K.DIALOG_FORM_IDS.DASHBOARD_SETTINGS_MODAL_FORM_ID}
        testId={ELEMENT.renameDashboardDialogOk}
      >
        OK
      </Button>,
      <Button variant={ButtonVariant.SECONDARY} onClick={closeDialog}>
        Cancel
      </Button>,
    ],
  };

  return (
    <Dialog title="Rename Dashboard" show={showModal} close={handleClose} footer={buttons} scrollable={true}>
      <div className="DashboardSettings">
        <BootstrapForm
          noValidate
          validated={validated}
          onSubmit={handleSubmit}
          id={K.DIALOG_FORM_IDS.DASHBOARD_SETTINGS_MODAL_FORM_ID}
        >
          <BootstrapForm.Group controlId="newName">
            <BootstrapForm.Label>Name</BootstrapForm.Label>
            <BootstrapForm.Control
              required // HTML5 validation rule
              autoComplete="off"
              placeholder="What to call this dashboard"
              value={name}
              name="name"
              type="text"
              onChange={onChangeName}
              data-test-id={ELEMENT.renameDashboardDialogInput}
            />
            <BootstrapForm.Control.Feedback type="invalid">
              Please enter a name for this dashboard.
            </BootstrapForm.Control.Feedback>
            <div className="invalid-feedback" style={{ display: validated && apiError ? 'block' : 'none' }}>
              {apiError}
            </div>
          </BootstrapForm.Group>

          <BootstrapForm.Group className="DashboardSettings__Description">
            <BootstrapForm.Label>Description</BootstrapForm.Label>
            <BootstrapForm.Control
              placeholder="Optional description"
              value={description}
              name="desc"
              type="text"
              as="textarea"
              rows={3}
              onChange={onChangeDescription}
            />
          </BootstrapForm.Group>
        </BootstrapForm>
      </div>
    </Dialog>
  );
}

export default DashboardSettings;
